import FamilySelectionBox from '@/components/FamilySelectionBox';
import GoBackMenu from '@/components/GoBackNavbar';
import SelectCategoryForm from '@/components/SelectCategoryForm';
import TagsInput from '@/components/TagsInput';
import Upload from '@/components/Upload';
import Constants from '@/Constants';
import Category from '@/models/Category';
import { LanguageType } from '@/models/LanguageType';
import { Lesson } from '@/models/Lesson';
import List from '@/models/List';
import { ApplicationState } from '@/store';
import { Identity } from '@/store/identity/types';
import { postListClear, postListRequest } from '@/store/list/action';
import { LocaleData } from '@/translations/LocaleData';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { formErrors, formInitialState, schema } from './constants';
import './style.scss';

const CreateList: React.FC = () => {
    const strings: LocaleData = useSelector((state: ApplicationState) => state.locale.data);
    const identity: Identity = useSelector((state: ApplicationState) => state.identity.data);
    const success: boolean = useSelector((state: ApplicationState) => state.lists.success);

    const [{ mainImage, name, author, description, locale, category, category2, tags, families }, setFormState] = useState(formInitialState);
    const [{ nameError, authorError, localeError, tagsError }, setFormError] = useState(formErrors);
    const [language, setLanguage] = useState<LanguageType>('');
    const [canPublish, setCanPublish] = useState<boolean>(false);
    const [categories, setCategories] = useState<Category[]>([]);

    const history = useHistory();

    const dispatch = useDispatch();

    const handleCreate = async () => {
        await handleSaveAndClose(false);
    };

    const handleSaveAndClose = async (willClose: boolean = true) => {
        setCanPublish(false);
        try {
            setFormError(formErrors);

            await schema.validate(
                {
                    mainImage,
                    name,
                    author,
                    locale,
                    categories,
                    tags,
                },
                { abortEarly: false },
            );

            const LIST: List = {
                applicationId: Constants.APP_ID,
                organizationId: families.length > 0 ? null : identity.user.orgId,
                locale: language,
                categories,
                tags,
                type: 'REGULAR',
                name,
                author,
                description,
                mainImage,
                bizId: `${name}_${new Date().getTime().toString()}`,
                published: false,
                totalRating: 0,
                visibility: false,
                status: 'DRAFT',
                rating: 0,
                currentLesson: {} as Lesson,
                availableTranslations: [],
                moduleId: Constants.PLAYLIST_MODULE_ID,
                families,
            };

            dispatch(postListRequest({ list: LIST, willClose, history }));
        } catch (err) {
            if (err.inner) {
                err.inner.map((error: any) =>
                    setFormError((prevState) => ({
                        ...prevState,
                        [`${error.path}Error`]: true,
                    })),
                );
            } else {
                console.error(err);
            }
        }
    };

    const handleOnChange = useCallback((fieldName: string, value: any) => {
        setFormState((prevState) => ({ ...prevState, [fieldName]: value }));
    }, []);

    useEffect(() => {
        if (success) {
            dispatch(postListClear());
        }
    }, [dispatch, success]);

    useEffect(() => {
        setCanPublish(Boolean(mainImage && name && author && language && category?.id && tags.length > 0));
    }, [mainImage, name, author, language, category, tags]);

    useEffect(() => {
        const updatedCategories: Category[] = [];

        if (category?.id) {
            updatedCategories.push(category);
        }

        if (category2?.id) {
            updatedCategories.push(category2);
        }

        setCategories(updatedCategories);
    }, [category, category2]);

    return (
        <>
            <div className="create-list">
                <GoBackMenu confirmable goBackPath={'/contents/playlist/lists'} />

                <div className="title-up">
                    <div className="title">
                        <h1>{`${strings.NEW_2} ${strings.LIST}`}</h1>
                    </div>
                </div>

                <div className="content body-content">
                    <div className="ml-5 mr-5 mt-4">
                        <FamilySelectionBox disabled={false} familyId={families[0]?.id} handleOnChange={handleOnChange} />
                    </div>

                    <div className="input-section">
                        <h1>
                            {strings.HEADER}
                            <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                        </h1>
                        <div className="ml-5 mr-5">
                            <Upload
                                onComplete={(e) => handleOnChange('mainImage', e.key)}
                                onRemove={() => handleOnChange('mainImage', undefined)}
                                previewFile={{ uri: mainImage, name: mainImage }}
                                typeAccepted="IMAGE"
                            />
                        </div>
                    </div>

                    <div className="row input-section">
                        <div className="title-container">
                            <h1>
                                {strings.TITLE}
                                <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                            </h1>
                            {nameError && <h1 className="error-message">{strings.ERROR_MESSAGE}</h1>}
                        </div>
                        <input
                            className={`form-control ${nameError ? 'error' : ''}`}
                            id="titleInput"
                            onChange={(e) => handleOnChange('name', e.target.value)}
                            placeholder={strings.TYPE_TITLE}
                            required
                            type="text"
                            value={name || ''}
                        />
                    </div>

                    <div className="row input-section">
                        <div className="title-container">
                            <h1>
                                {strings.AUTHOR}
                                <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                            </h1>
                            {authorError && <h1 className="error-message">{strings.ERROR_MESSAGE}</h1>}
                        </div>
                        <input
                            className={`form-control ${authorError ? 'error' : ''}`}
                            id="titleInput"
                            onChange={(e) => handleOnChange('author', e.target.value)}
                            placeholder={strings.TYPE_AUTHOR}
                            required
                            type="text"
                            value={author || ''}
                        />
                    </div>

                    <div className="row input-section">
                        <div className="title-container">
                            <h1>{strings.ABOUT}</h1>
                        </div>
                        <textarea
                            className="form-control"
                            id="aboutInput"
                            onChange={(e) => handleOnChange('description', e.target.value)}
                            placeholder={strings.ABOUT_INSERT}
                            rows={3}
                            value={description || ''}
                        />
                    </div>

                    <div className="row input-section">
                        <div className="title-container">
                            <h1>
                                {strings.SELECT_LANGUAGE}
                                <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                            </h1>
                            {localeError && <h1 className="error-message">{strings.ERROR_MESSAGE}</h1>}
                        </div>
                        <div className="container button-group-content-type">
                            <button
                                className={`btn btn-primary btn-lg ${language === 'pt' ? 'active' : ''} ${localeError ? 'error' : ''}}`}
                                onClick={() => setLanguage('pt')}
                                type="button"
                            >
                                <span className="align-middle">{strings.LANGUAGE_PT}</span>
                            </button>

                            <button
                                className={`btn btn-primary btn-lg ${language === 'en' ? 'active' : ''}`}
                                onClick={() => setLanguage('en')}
                                type="button"
                            >
                                <span className="align-middle">{strings.LANGUAGE_EN}</span>
                            </button>
                        </div>
                    </div>

                    <div className="list-category-section required">
                        <SelectCategoryForm
                            categoriesForm={categories}
                            familyId={families[0]?.id}
                            handleOnChange={(fieldName, categories) => {
                                const [category, category2] = categories || [];
                                handleOnChange('category', category);
                                handleOnChange('category2', category2);
                            }}
                            isColumnFormat
                        />
                    </div>

                    <div className="row input-section">
                        <div className="d-flex flex-fill flex-column">
                            <h1>
                                {strings.TAGS}
                                <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                            </h1>
                            <TagsInput hasError={tagsError} setTags={handleOnChange} />
                        </div>
                    </div>

                    <div className="warning-input">
                        <p className="warning-text">
                            {Constants.symbol.ASTERISK}
                            {strings.REQUIRED_FIELDS}
                        </p>
                    </div>

                    <div className="d-flex vertical-center justify-content-end h-20 w-100 submit-buttons">
                        <button className="btn btn-link" data-target="#alert-modal" data-toggle="modal" type="button">
                            {strings.CANCEL}
                        </button>
                        <button
                            className="btn btn-primary btn-sm save-close"
                            disabled={!canPublish}
                            onClick={() => handleSaveAndClose()}
                            type="button"
                        >
                            {strings.SAVE_AND_CLOSE}
                        </button>
                        <button className="btn btn-primary btn-sm" disabled={!canPublish} onClick={handleCreate} type="button">
                            {strings.CREATE}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CreateList;
